import { IManagedUserRecentSummary } from '@/types';
import { ISortConfig } from '@/components/Tables';
import { useCallback } from 'react';

/** Props for the useManagerDashboardSorting hook */
interface UseManagerDashboardSortingProps {
  /** The current list of filtered users to sort */
  filteredUsers: IManagedUserRecentSummary[];
  /** Function to update the filtered users state */
  setFilteredUsers: (users: IManagedUserRecentSummary[]) => void;
  /** Function to update the sort configuration state */
  setSortConfig: (config: ISortConfig) => void;
}

/**
 * Hook to manage table sorting logic for the Manager Dashboard.
 * Handles column header clicks and sorts the filtered users accordingly,
 * placing null values at the end of the sorted list.
 */
export const useManagerDashboardSorting = ({
  filteredUsers,
  setFilteredUsers,
  setSortConfig,
}: UseManagerDashboardSortingProps) => {
  const handleSort = useCallback(
    (key: string, direction: ISortConfig['direction']): void => {
      const sortedUsers = [...filteredUsers].sort((a, b) => {
        const aValue = (a as any)[key];
        const bValue = (b as any)[key];

        if (aValue === null) return 1;
        if (bValue === null) return -1;
        if (aValue === bValue) return 0;

        return aValue > bValue ? 1 : -1;
      });

      if (direction === 'desc') {
        sortedUsers.reverse();
      }

      setFilteredUsers(sortedUsers);
      setSortConfig({ key, direction });
    },
    [filteredUsers, setFilteredUsers, setSortConfig],
  );

  return { handleSort };
};
